import { isNil, isString } from "lodash-es";
import type { HTMLContent, JSONContent } from "@tiptap/core";
import FieldValueReference from "../fields/FieldValueReference";
import type { Delimiter, StringTemplate } from "./stringTemplateUtil";
import type { TFunction } from "i18next";
import type { ValueReference } from "../types/ValueReference";
import VariableValueReference from "../variables/VariableValueReference";
import { flattenOptions } from "./menu/searchableMenuUtil";
import type { DynamicValueOptionType, SelectOptions } from "../types/Menu";
import MetaVariableReference from "../metaVariables/MetaVariableReference";
import type { MetaVariableId } from "../metaVariables/metaVariable";

const REGEX_PLACEHOLDER = /([^${]*?)\w(?=})/g;
const REGEX_PLACEHOLDER_WITH_DOT = /([^${]*?)\w(?=\.)/g;
export const getPlaceholderDataNames = (value?: string): string[] => [
  ...Array.from(value?.matchAll(REGEX_PLACEHOLDER) ?? [], (m) => m[0]),
  ...Array.from(value?.matchAll(REGEX_PLACEHOLDER_WITH_DOT) ?? [], (m) => m[0]),
];

// A pattern that matches (nested) placeholders, for example; ${sys.user}
export const PLACEHOLDER_PATTERN = /\${([.\w]+)}/;
export const PLACEHOLDER_PATTERN_WITHOUT_DOLLAR = /\{([.\w]+)}/;

export const placeholderPattern = (delimiter: Delimiter) => {
  switch (delimiter) {
    case "{":
      return PLACEHOLDER_PATTERN_WITHOUT_DOLLAR;
    case "${":
      return PLACEHOLDER_PATTERN;
  }
};

export const inferDynamicValueOptionType = (value: unknown): DynamicValueOptionType => {
  if (value instanceof FieldValueReference && value.type === "image") {
    return "image";
  }
  return "text";
};

const createPillComponentElement = <T>(
  type: "field" | "variable" | "metaVariable",
  value: T,
  icon: string,
  label: string,
  orphaned: boolean,
) =>
  // Do not make this component self-closing, this will break
  `<pill-component type="${type}" valuetype="${inferDynamicValueOptionType(value) ?? "text"}" value="${value}" iconname="${icon}" label="${label}" orphaned="${orphaned}"></pill-component>`;

export const toTipTapContent = <T>(value: StringTemplate, options: SelectOptions<T>, t: TFunction): HTMLContent =>
  value
    .map(({ type, value }) => {
      if (type === "literal") {
        return value;
      }

      const flattenedOptions = flattenOptions(options);
      if (type === "field" || type === "variable" || type === "metaVariable") {
        const option = flattenedOptions.find((option) => value.equals(option.value as never));
        if (!isNil(option)) {
          if (option.variant !== "dynamicValue" || isNil(option.icon)) {
            return;
          }
          return createPillComponentElement<T>(type, option.value, option.icon.name, option.label, false);
        }
      }

      // Because we transform the content to a string we lose reactivity and the label will not change upon language change
      return createPillComponentElement(
        "field",
        value,
        "exclamationCircle",
        t(($) => $.FIELD_PROPERTY_SUBFORM_UNKNOWN_PLACEHOLDER, { value }),
        true,
      );
    })
    .join("");

type PillComponentAttrs = JSONContent & {
  attrs: {
    type: "field" | "variable" | "metaVariable" | "text";
    orphaned: boolean;
    value: string | ValueReference;
  };
};

const isPillComponent = (item: JSONContent): item is PillComponentAttrs => item.type === "pillComponent";

export const fromTipTapContent = (content: JSONContent[]): StringTemplate =>
  content
    .map((item) => {
      if (isPillComponent(item)) {
        if (item.attrs.orphaned) {
          return { type: "unknown" as const, value: item.attrs.value as string };
        }

        if (item.attrs.type === "field") {
          const value = isString(item.attrs.value)
            ? FieldValueReference.from(item.attrs.value)
            : (item.attrs.value as FieldValueReference);
          return { type: "field" as const, value };
        }

        if (item.attrs.type === "variable") {
          const value = isString(item.attrs.value)
            ? VariableValueReference.from(item.attrs.value)
            : (item.attrs.value as VariableValueReference);
          return { type: "variable" as const, value };
        }

        if (item.attrs.type === "metaVariable") {
          const value = isString(item.attrs.value)
            ? MetaVariableReference.from(item.attrs.value as MetaVariableId)
            : (item.attrs.value as MetaVariableReference);
          return { type: "metaVariable" as const, value };
        }
      } else if (item.type === "text") {
        return { type: "literal" as const, value: item.text as string };
      } else if (item.type === "inputComponent") {
        return undefined;
      }

      throw new Error(`Unknown type ${item.type}`);
    })
    .filter((value) => !isNil(value));

export const replacePillWithPlaceholder = (text: string, delimiter: Delimiter = "${"): string => {
  const pillOpenPattern = /<pill-component\s+(?=[^>]*\bvalue="([^"]+)")(?=[^>]*\bvaluetype="([^"]+)")[^>]*>/g;
  const pillClosePattern = "</pill-component>";

  const replacePillOpen = (match: string, value: string, valueType: DynamicValueOptionType): string => {
    if (valueType === "image") {
      return `<img src="${delimiter}${value}}">`;
    }
    return `${delimiter}${value}}`;
  };
  return text.replaceAll(pillOpenPattern, replacePillOpen).replaceAll(pillClosePattern, "");
};
